/**
 * Collections Browser - Firestore 컬렉션 브라우저
 */

import { Trans, t } from '@lingui/macro';
import {
    Database, ChevronRight, RefreshCw, Loader2, Search,
    Eye, FileText
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, query, limit, orderBy, doc, getDoc } from 'firebase/firestore';

interface CollectionInfo {
    name: string;
    path: string;
    docCount: number;
    sampleDoc?: Record<string, any>;
}

const COLLECTION_PATHS = [
    'workspaces',
    'rooms',
    'messages',
    'users',
    'notifications',
    'roominvites',
    'roomcalls',
    'userreports',
    'admin/tiers/items',
    'admin/promotions/items',
    'admin/super_admins/accounts',
];

const DevAdminCollections: React.FC = () => {
    const [collections, setCollections] = useState<CollectionInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCollection, setSelectedCollection] = useState<CollectionInfo | null>(null);
    const [documents, setDocuments] = useState<any[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);

    useEffect(() => {
        loadCollections();
    }, []);

    const loadCollections = async () => {
        setLoading(true);
        const results: CollectionInfo[] = [];

        for (const path of COLLECTION_PATHS) {
            try {
                const snap = await getDocs(query(collection(Fire.store, path), limit(1)));
                results.push({
                    name: path.split('/').pop() || path,
                    path,
                    docCount: snap.size > 0 ? -1 : 0, // -1 = 문서 있음 (정확한 수는 별도 조회 필요)
                    sampleDoc: snap.docs[0]?.data(),
                });
            } catch (error) {
                console.error(`Failed to access ${path}:`, error);
                results.push({
                    name: path.split('/').pop() || path,
                    path,
                    docCount: -2, // 에러
                });
            }
        }

        setCollections(results);
        setLoading(false);
    };

    const loadCollectionDocs = async (col: CollectionInfo) => {
        setSelectedCollection(col);
        setLoadingDocs(true);

        try {
            const snap = await getDocs(query(collection(Fire.store, col.path), limit(20)));
            const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDocuments(docs);
        } catch (error) {
            console.error(`Failed to load docs from ${col.path}:`, error);
            setDocuments([]);
        } finally {
            setLoadingDocs(false);
        }
    };

    const filteredCollections = collections.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.path.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return 'null';
        if (typeof value === 'object') {
            if (value.toDate) return value.toDate().toLocaleString();
            return JSON.stringify(value).slice(0, 50) + '...';
        }
        const str = String(value);
        return str.length > 50 ? str.slice(0, 50) + '...' : str;
    };

    return (
        <div className="flex h-full">
            {/* Collections List */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-100">
                    <h2 className="font-semibold text-slate-900 mb-3">
                        <Trans>Collections</Trans>
                    </h2>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder={t`Search...`}
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredCollections.map((col) => (
                                <button
                                    key={col.path}
                                    onClick={() => loadCollectionDocs(col)}
                                    className={`w-full p-3 text-left hover:bg-slate-50 transition-colors ${selectedCollection?.path === col.path ? 'bg-emerald-50' : ''
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Database className="w-4 h-4 text-slate-400" />
                                        <span className="font-medium text-slate-900">{col.name}</span>
                                        {col.docCount === -2 && (
                                            <Badge variant="destructive" className="text-meta-sm">Error</Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5 ml-6">
                                        {col.path}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Documents View */}
            <div className="flex-1 flex flex-col bg-slate-50">
                {selectedCollection ? (
                    <>
                        <div className="bg-white border-b border-slate-200 px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-slate-900">
                                        {selectedCollection.name}
                                    </h3>
                                    <p className="text-sm text-slate-500">{selectedCollection.path}</p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => loadCollectionDocs(selectedCollection)}
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            {loadingDocs ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                                </div>
                            ) : documents.length === 0 ? (
                                <div className="text-center py-10 text-slate-500">
                                    <Trans>No documents found</Trans>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {documents.map((doc) => (
                                        <Card key={doc.id} className="cursor-pointer hover:shadow-sm">
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-mono text-sm text-slate-700 mb-2">
                                                            {doc.id}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {Object.entries(doc).slice(0, 6).map(([key, value]) => (
                                                                key !== 'id' && (
                                                                    <div key={key} className="flex gap-1">
                                                                        <span className="text-slate-400">{key}:</span>
                                                                        <span className="text-slate-600 truncate">
                                                                            {formatValue(value)}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" size="icon">
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-slate-400">
                        <div className="text-center">
                            <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p><Trans>Select a collection to view documents</Trans></p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminCollections;
